import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { ListMyTeamSetlistsQuery, useListMyTeamSetlistsQuery } from '@/graphql';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	readProviderCacheUpdatedAt,
	writeProviderCache,
} from '@/helpers/providerCache';
import { formatISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { groupBy, isNil } from 'ramda';

import { LoadingResource, useLoading } from '../LoadingProvider';

export interface SetlistsContext {
	isLoading: boolean;
	isRefetching: boolean;
	error: Error | null;
	setlists: ListMyTeamSetlistsQuery['listMyTeamSetlists'];
	previousSetlists: ListMyTeamSetlistsQuery['listMyTeamSetlists'];
	futureSetlists: ListMyTeamSetlistsQuery['listMyTeamSetlists'];
	draftSetlists: ListMyTeamSetlistsQuery['listMyTeamSetlists'];
	setlistsByDate: Record<
		string,
		ListMyTeamSetlistsQuery['listMyTeamSetlists']
	>;
	dataUpdatedAt: number;
	refetch: () => void;
}

const defaultValue: SetlistsContext = {
	error: null,
	isLoading: false,
	isRefetching: false,
	setlists: [],
	previousSetlists: [],
	futureSetlists: [],
	draftSetlists: [],
	setlistsByDate: {},
	dataUpdatedAt: 0,
	refetch: () => null,
};

const SetlistsContext = createContext<SetlistsContext>(defaultValue);

export function useSetlists(): SetlistsContext {
	const context = useContext(SetlistsContext);
	if (isNil(context)) {
		throw new Error(`useSetlists must be used within a SetlistsProvider`);
	}

	return context;
}

const byDate = groupBy<ListMyTeamSetlistsQuery['listMyTeamSetlists'][0]>(
	function (chat) {
		const date = new Date(chat.startAt);
		const day = formatISO(date, { representation: 'date' });
		return day;
	},
);

export const SetlistsProvider: React.FC = ({ children }) => {
	const { enqueueSnackbar } = useSnackbar();
	const { setLoaded } = useLoading();
	const cachedSetlists = readProviderCache<ListMyTeamSetlistsQuery>(
		PROVIDER_CACHE_KEYS.setlists,
	);
	const { data, isLoading, error, refetch, dataUpdatedAt, isRefetching } =
		useListMyTeamSetlistsQuery<ListMyTeamSetlistsQuery, Error>(
			{},
			{
				initialData: cachedSetlists ?? undefined,
				initialDataUpdatedAt: readProviderCacheUpdatedAt(
					PROVIDER_CACHE_KEYS.setlists,
				),
				refetchOnMount: true,
				refetchOnWindowFocus: true,
			},
		);

	const today = useMemo(() => {
		const date = new Date();
		date.setHours(0);
		date.setMinutes(0);
		date.setSeconds(0);
		return date;
		// We need to trigger a recalculation when the data changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataUpdatedAt]);

	const setlists = useMemo(() => {
		if (!data) {
			return [];
		}
		return data?.listMyTeamSetlists;
		// We need to trigger a recalculation when the data changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dataUpdatedAt]);

	const draftSetlists = useMemo(() => {
		if (!data) {
			return [];
		}
		return data?.listMyTeamSetlists.filter((s) => s.draft);
		// We need to trigger a recalculation when the data changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dataUpdatedAt]);

	const setlistsByDate = useMemo(() => {
		if (!data) {
			return {};
		}
		return byDate(data.listMyTeamSetlists);
		// We need to trigger a recalculation when the data changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dataUpdatedAt]);

	const futureSetlists = useMemo(() => {
		return setlists
			.filter((s) => new Date(s.startAt) > today)
			.filter((s) => !s.feedback);
	}, [setlists, today]);

	const previousSetlists = useMemo(() => {
		return setlists
			.filter((s) => new Date(s.startAt) < today || !!s.feedback)
			.sort((a, b) => {
				const aStartAt = new Date(a.startAt || 0);
				const bStartAt = new Date(b.startAt || 0);

				return bStartAt.getTime() - aStartAt.getTime();
			});
	}, [setlists, today]);

	useEffect(() => {
		if (!isLoading) {
			setLoaded(LoadingResource.SETLISTS);
		}
	}, [isLoading, setLoaded]);

	useEffect(() => {
		if (data) {
			writeProviderCache(PROVIDER_CACHE_KEYS.setlists, data);
		}
	}, [data]);

	if (error) {
		enqueueSnackbar('Failed to load setlists', {
			variant: 'warning',
		});
	}

	return (
		<SetlistsContext.Provider
			value={{
				isLoading,
				isRefetching,
				setlists,
				futureSetlists,
				previousSetlists,
				draftSetlists,
				setlistsByDate,
				error,
				dataUpdatedAt,
				refetch,
			}}
		>
			{children}
		</SetlistsContext.Provider>
	);
};

export default SetlistsProvider;
